import { createFileRoute, useSearch } from "@tanstack/react-router";
import { AdministratorsTable } from "./-components/administrator-accounts-table";
import { useContext, useEffect, useState } from "react";
import { DashboardContext } from "@/components/dashboard-layout";
import { AdministratorDetails } from "./-components/administrator-accounts-sidebar";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { administratorAccountsQueryOptions, administratorProfilesQueryOptions } from "@/server/administrators";
import { toast } from "sonner";

export const Route = createFileRoute("/_dashboard/administrators/accounts")({
  component: RouteComponent,
  loader: ({ context }) => {
    return {
      crumb: "Administrator accounts",
      currentUsername: (context as { user?: { username: string } }).user?.username,
    };
  },
});

function RouteComponent() {
  const navigate = useNavigate();
  const { currentUsername } = Route.useLoaderData();
  const searchParams = useSearch({ strict: false }) as {
    id?: string;
  };
  const location = useLocation();
  const context = useContext(DashboardContext);

  const administratorAccountsQuery = useQuery(administratorAccountsQueryOptions);
  const administrators = administratorAccountsQuery.data || [];
  const administratorProfilesQuery = useQuery(administratorProfilesQueryOptions);
  const profiles = administratorProfilesQuery.data || [];

  const [isContextReady, setIsContextReady] = useState(false);

  useEffect(() => {
    if (administratorAccountsQuery.isError) {
      const message =
        administratorAccountsQuery.error instanceof Error
          ? administratorAccountsQuery.error.message
          : "Failed to load administrator accounts. Please try again later.";
      toast.error(message);
    }
  }, [administratorAccountsQuery.isError, administratorAccountsQuery.error]);

  useEffect(() => {
    if (administratorProfilesQuery.isError) {
      const message =
        administratorProfilesQuery.error instanceof Error
          ? administratorProfilesQuery.error.message
          : "Failed to load administrator profiles. Please try again later.";
      toast.error(message);
    }
  }, [administratorProfilesQuery.isError, administratorProfilesQuery.error]);

  // Check if context is available
  if (!context) {
    console.error("DashboardContext is null. Ensure DashboardLayout wraps this component.");
    return <div>Error: DashboardContext is not available.</div>;
  }

  const isExact = location.href === "/administrators/accounts" || location.href === "/administrators/accounts/";

  // When the page loads with the sidebar open, the context might not be ready and the sidebar will thus not be set.
  // This effect ensures that the sidebar is set when the context is ready.
  useEffect(() => {
    if (context) {
      setIsContextReady(true);
    }
  }, [context]);

  useEffect(() => {
    if (!isContextReady || !context?.setRightSidebar) {
      return;
    }

    const currentSidebar = context.rightSidebar;

    if (isExact && currentSidebar) {
      context.setRightSidebar(null);
    } else if (searchParams.id && administrators && administrators.length) {
      const admin = administrators.find((admin) => admin.id?.toString() == searchParams.id);
      if (admin) {
        const newSidebar = (
          <AdministratorDetails administrator={admin} profiles={profiles} currentUsername={currentUsername} />
        );
        if (currentSidebar !== newSidebar) {
          context.setRightSidebar(newSidebar);
        }
      } else if (searchParams.id === "new") {
        const newSidebar = (
          <AdministratorDetails administrator={null} profiles={profiles} currentUsername={currentUsername} />
        );
        if (currentSidebar !== newSidebar) {
          context.setRightSidebar(newSidebar);
        }
      }
      context.setRightSidebarParamKey?.("id");
    }
  }, [searchParams.id, administrators, isContextReady, profiles]);

  const handleAddAdmin = () => {
    navigate({
      to: "/administrators/accounts",
      replace: true,
      search: (prev) => ({ ...prev, id: "new" }),
    });
  };

  return (
    <AdministratorsTable
      administrators={administrators}
      handleAddAdmin={handleAddAdmin}
      isLoading={administratorAccountsQuery.isLoading || administratorProfilesQuery.isLoading}
      currentUsername={currentUsername}
    />
  );
}
